import type { CaseFilters } from "../../services";

export type CuttingTab = "active" | "additional";
export type CuttingSort = "received-asc" | "case-asc" | "case-desc";

export interface CuttingQueueView {
  filters: CaseFilters;
  sort: CuttingSort;
}

export const DEFAULT_CUTTING_VIEW: CuttingQueueView = {
  filters: {},
  sort: "received-asc",
};

const ARRAY_FILTERS = new Set([
  "statusIds",
  "currentStatusIds",
  "facilityIds",
  "pathologistIds",
  "workflowIds",
  "procedureIds",
  "patientIds",
  "flagDefinitionIds",
]);
const STRING_FILTERS = new Set([
  "physicianId",
  "dateFrom",
  "dateTo",
  "dateCollected",
  "search",
]);
const BOOLEAN_FILTERS = new Set([
  "isBlocked",
  "isRush",
  "isHighPriority",
  "isAdditionalStain",
]);
const SORTS = new Set<CuttingSort>(["received-asc", "case-asc", "case-desc"]);

function storageKey(userId: string, tab: CuttingTab): string {
  return `cutting_queue_view_v1:${userId}:${tab}`;
}

function decodeFilters(raw: unknown): CaseFilters {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const filters: Record<string, unknown> = {};
  Object.entries(raw).forEach(([key, value]) => {
    if (ARRAY_FILTERS.has(key) && Array.isArray(value)) {
      filters[key] = value.filter(
        (item): item is string => typeof item === "string" && item.length > 0
      );
    } else if (STRING_FILTERS.has(key) && typeof value === "string") {
      if (!key.startsWith("date") || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        filters[key] = value;
      }
    } else if (BOOLEAN_FILTERS.has(key) && typeof value === "boolean") {
      filters[key] = value;
    }
  });
  return filters as CaseFilters;
}

export function readCuttingQueueView(
  userId: string | undefined,
  tab: CuttingTab
): CuttingQueueView {
  if (!userId) return DEFAULT_CUTTING_VIEW;
  try {
    const stored = sessionStorage.getItem(storageKey(userId, tab));
    if (!stored) return DEFAULT_CUTTING_VIEW;
    const raw: unknown = JSON.parse(stored);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return DEFAULT_CUTTING_VIEW;
    }
    const record = raw as Record<string, unknown>;
    return {
      filters: decodeFilters(record.filters),
      sort: SORTS.has(record.sort as CuttingSort)
        ? (record.sort as CuttingSort)
        : DEFAULT_CUTTING_VIEW.sort,
    };
  } catch {
    return DEFAULT_CUTTING_VIEW;
  }
}

export function saveCuttingQueueView(
  userId: string | undefined,
  tab: CuttingTab,
  view: CuttingQueueView
): void {
  if (!userId) return;
  try {
    sessionStorage.setItem(storageKey(userId, tab), JSON.stringify(view));
  } catch {
    // The current view remains usable when browser storage is unavailable.
  }
}

export function clearCuttingQueueView(
  userId: string | undefined,
  tab: CuttingTab
): void {
  if (!userId) return;
  try {
    sessionStorage.removeItem(storageKey(userId, tab));
  } catch {
    // The current view can still be cleared in memory.
  }
}
